// ui/src/components/tenant-admin/sections/CreditAllocationSection.jsx

/**
 * @file CreditAllocationSection.jsx
 * @description Plan document section for Layer 0 credit allocation configuration.
 *
 * S810 REWRITE — OI-S810-SECTION-FORM-STATE
 *   - Owns local form state via useState (no onChange to parent during editing).
 *   - Exposes getValues() via useImperativeHandle for one-PATCH save flow.
 *   - useEffect resets form only when plan.planId changes.
 *   - defaultProps REMOVED (React 18 deprecated).
 *   - onChange prop REMOVED — replaced by section-local handlers.
 *
 * S819 — OI-S812-L0-RULE-SCHEMAS
 *   Fix 1: form.creditingMode now always holds server enum value
 *           (individual | deal_split | territory | overlay | channel).
 *           UI grouping (shared/routed/split) is presentational only.
 *           Single-type mode groups skip Step 2 and go directly to Step 3.
 *   Fix 2: Step 3 attribution rules forms wired per creditingMode with live
 *           dropdowns fetched from server. attributionRules is non-null object
 *           on Save (Decision #14 — partial values allowed through; Submit blocks).
 *
 * @module tenant-admin/sections
 * @author data-fit LLC · DecisionFlow · S775 / S810 / S819
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';

// ---------------------------------------------------------------------------
// UI grouping → server creditingMode mapping
// MODE_OPTIONS drives the Step 1 radio cards.
// value is the UI grouping key — NOT sent to server.
// serverMode is what gets stored in form.creditingMode.
// singleType: true → selecting this mode skips Step 2 and sets creditingMode immediately.
// ---------------------------------------------------------------------------
const MODE_OPTIONS = [
  {
    value:      'individual',
    serverMode: 'individual',
    singleType: true,
    label:       'Individual',
    description: 'One rep receives 100% credit.',
  },
  {
    value:      'shared',
    serverMode: 'deal_split',
    singleType: true,
    label:       'Shared',
    description: 'Multiple reps share credit for the same event.',
  },
  {
    value:      'routed',
    serverMode: 'territory',
    singleType: true,
    label:       'Routed',
    description: 'Credit flows through territory or hierarchy rules.',
  },
  {
    value:      'split',
    serverMode: null,       // requires Step 2 to pick overlay vs channel
    singleType: false,
    label:       'Split',
    description: 'Explicit deal-by-deal split at transaction time.',
  },
];

// Step 2 options — only rendered for the 'split' group (singleType: false)
const SPLIT_TYPE_OPTIONS = [
  {
    value:       'overlay',
    label:       'Overlay / Co-sell',
    description: 'Allocate transaction credit across overlay or co-sell participants.',
  },
  {
    value:       'channel',
    label:       'Channel / Partner',
    description: 'Allocate transaction credit for channel or partner participation.',
  },
];

// Server enum → UI group value (used to restore radio state from saved plan)
const SERVER_MODE_TO_GROUP = {
  individual: 'individual',
  deal_split: 'shared',
  territory:  'routed',
  overlay:    'split',
  channel:    'split',
};

// Commission basis options for channel mode — grounded from PartnerCompPlanCreate
const COMMISSION_BASIS_OPTIONS = [
  { value: '',          label: 'Select commission basis' },
  { value: 'revenue',   label: 'Revenue' },
  { value: 'margin',    label: 'Margin' },
  { value: 'flat_fee',  label: 'Flat fee' },
];

// ---------------------------------------------------------------------------
// initFromPlan — derives form state from normalized layer0Config.
// creditingMode is already the server enum value from normalizeLayer0Config.
// ---------------------------------------------------------------------------
function initFromPlan(layer0Config) {
  return {
    creditingMode:    layer0Config.creditingMode    ?? 'individual',
    layer0Type:       layer0Config.layer0Type       ?? null,
    attributionRules: layer0Config.attributionRules ?? null,
    // isActive REMOVED — S822 OI-S821-LAYER0-ISACTIVE-FIELD.
    // isActive belongs to plan_component, not layer0_config JSONB.
    // Sending it in the PATCH body pollutes the JSONB column with a field
    // the server schema does not define for layer0Config. Never re-add.
  };
}

// Derive the UI group from a server creditingMode value
function groupFromMode(serverMode) {
  return SERVER_MODE_TO_GROUP[serverMode] ?? 'individual';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CreditAllocationSection = forwardRef(function CreditAllocationSection({
  plan,
  layer0Config,
  readOnly,
  validationErrors = {},
}, ref) {
  const [form, setForm]               = useState(() => initFromPlan(layer0Config));
  const [localErrors, setLocalErrors] = useState({});
  const [expanded, setExpanded]       = useState(true);
  // selectedGroup tracks which Step 1 radio card is active.
  // Separate from form.creditingMode because 'split' group has no server enum value
  // until the user picks overlay or channel in Step 2.
  const [selectedGroup, setSelectedGroup] = useState(
    () => groupFromMode(layer0Config.creditingMode ?? 'individual')
  );

  // Attribution option lists — fetched per mode
  const [territories,     setTerritories]     = useState([]);
  const [overlayRules,    setOverlayRules]    = useState([]);
  const [partnerPlans,    setPartnerPlans]    = useState([]);
  const [optionsLoading,  setOptionsLoading]  = useState(false);
  const [optionsError,    setOptionsError]    = useState(null);

  // Reset form only on plan change
  useEffect(() => {
    setForm(initFromPlan(layer0Config));
    setLocalErrors({});
    setSelectedGroup(groupFromMode(layer0Config.creditingMode ?? 'individual'));
  }, [plan.planId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (readOnly) setExpanded(true);
  }, [readOnly]);

  // ---------------------------------------------------------------------------
  // Fetch attribution options when creditingMode changes.
  // Each mode fetches its own reference data list. AbortController cancels
  // stale requests when mode changes before fetch completes (Rule 7 — no
  // network calls on onChange; these fire only on mode selection, not typing).
  // ---------------------------------------------------------------------------
  const fetchOptionsForMode = useCallback((mode) => {
    if (mode === 'individual') return;

    const controller = new AbortController();
    const signal = controller.signal;
    setOptionsLoading(true);
    setOptionsError(null);

    const fetchOpts = { credentials: 'include', signal };

    const doFetch = async () => {
      try {
        if (mode === 'territory') {
          const res = await fetch('/api/v1/territories?is_active=true', fetchOpts);
          if (!res.ok) throw new Error(`territories fetch failed (${res.status})`);
          const data = await res.json();
          setTerritories(data.items ?? []);

        } else if (mode === 'overlay') {
          // overlay_attribution_router GET requires overlay_position_id as a mandatory
          // filter — no list-all endpoint exists. Fetch positions instead; CA-1 selects
          // the overlay position and the attribution rule is created downstream.
          const res = await fetch('/api/v1/positions', fetchOpts);
          if (!res.ok) throw new Error(`positions fetch failed (${res.status})`);
          const data = await res.json();
          setOverlayRules((data.positions ?? []).filter((p) => p.is_active));

        } else if (mode === 'channel') {
          const res = await fetch('/api/v1/partner-comp-plans?is_active=true', fetchOpts);
          if (!res.ok) throw new Error(`partner-comp-plans fetch failed (${res.status})`);
          const data = await res.json();
          setPartnerPlans(data.items ?? []);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setOptionsError(err.message ?? 'Failed to load options.');
      } finally {
        setOptionsLoading(false);
      }
    };

    doFetch();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (form.creditingMode === 'individual') return;
    const cleanup = fetchOptionsForMode(form.creditingMode);
    return cleanup;
  }, [form.creditingMode, fetchOptionsForMode]);

  // ---------------------------------------------------------------------------
  // getValues — called by PlanDocumentCanvas on Save button click.
  // Decision #14: structural integrity only at Save.
  // Non-individual mode must have creditingMode + layer0Type set.
  // attributionRules partial values are allowed through (semantic check at Submit).
  // Returns null to abort save only when creditingMode/layer0Type are structurally missing.
  // ---------------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    getValues() {
      if (form.creditingMode !== 'individual') {
        const errors = {};
        if (!form.layer0Type) {
          errors.layer0Type = 'A credit allocation type is required for non-individual crediting modes.';
        }
        // attributionRules null is now only possible if user selected a mode but
        // picked no option from the dropdown — treat as structural incompleteness
        // since the object cannot be meaningfully empty.
        if (!form.attributionRules || Object.keys(form.attributionRules).length === 0) {
          errors.attributionRules = 'Attribution rules are required before this configuration can be saved.';
        }
        if (Object.keys(errors).length > 0) {
          setLocalErrors(errors);
          return null;
        }
      }
      setLocalErrors({});
      return { layer0Config: form };
    },
  }), [form]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  // Step 1 — mode group card selected
  const handleModeChange = (option) => {
    setLocalErrors({});
    setSelectedGroup(option.value);
    if (option.singleType) {
      // Single-type group: set server enum immediately, skip Step 2
      setForm((prev) => ({
        ...prev,
        creditingMode:    option.serverMode,
        layer0Type:       option.serverMode === 'individual' ? null : option.serverMode,
        attributionRules: null,
      }));
    } else {
      // Multi-type group (split): creditingMode stays null until Step 2 resolves it
      setForm((prev) => ({
        ...prev,
        creditingMode:    null,
        layer0Type:       null,
        attributionRules: null,
      }));
    }
    if (option.value !== 'individual') setExpanded(true);
  };

  // Step 2 — type selected within 'split' group
  const handleTypeChange = (e) => {
    const nextMode = e.target.value || null;
    setLocalErrors((prev) => ({ ...prev, layer0Type: null }));
    setForm((prev) => ({
      ...prev,
      creditingMode:    nextMode,
      layer0Type:       nextMode,
      attributionRules: null,
    }));
  };

  // Step 3 — attribution rule field changed
  const handleAttributionChange = (field, value) => {
    setLocalErrors((prev) => ({ ...prev, attributionRules: null }));
    setForm((prev) => ({
      ...prev,
      attributionRules: {
        ...(prev.attributionRules ?? {}),
        [field]: value || null,
      },
    }));
  };

  // ---------------------------------------------------------------------------
  // Derived display values
  // ---------------------------------------------------------------------------
  // activeGroup is now driven by selectedGroup (explicit UI state) not by
  // form.creditingMode — which is null for 'split' group until Step 2 resolves.
  const activeGroup = selectedGroup;
  const activeGroupOption = MODE_OPTIONS.find((o) => o.value === activeGroup) ?? MODE_OPTIONS[0];

  const summaryType = (() => {
    if (!form.creditingMode || form.creditingMode === 'individual') return 'Individual · 100% credit';
    const modeLabel = activeGroupOption.label;
    const typeLabel = SPLIT_TYPE_OPTIONS.find((o) => o.value === form.creditingMode)?.label ?? null;
    if (form.creditingMode === 'deal_split') return `${modeLabel} · Deal Split`;
    if (form.creditingMode === 'territory')  return `${modeLabel} · Territory`;
    return typeLabel ? `${modeLabel} · ${typeLabel}` : modeLabel;
  })();

  const errorFor = (field) => validationErrors?.[field] || localErrors?.[field] || null;
  const errorId  = (field) => `credit-allocation-${field}-error`;

  // Is the 'split' group currently selected (needing Step 2)?
  const isSplitGroup = activeGroup === 'split';
  // Does Step 3 apply (mode fully resolved)?
  const showStep3 = form.creditingMode && form.creditingMode !== 'individual' && form.layer0Type;

  // ---------------------------------------------------------------------------
  // Step 3 form blocks per creditingMode
  // ---------------------------------------------------------------------------
  const renderAttributionForm = () => {
    if (optionsLoading) {
      return (
        <p className="modifier-manager-info" role="status" aria-live="polite">
          Loading options…
        </p>
      );
    }
    if (optionsError) {
      return (
        <p className="modifier-manager-warning" role="alert">
          Could not load options: {optionsError}
        </p>
      );
    }

    const rules = form.attributionRules ?? {};

    if (form.creditingMode === 'deal_split') {
      return (
        <div>
          <p className="credit-allocation-option-copy" style={{ marginBottom: 12 }}>
            Deal split crediting distributes credit across multiple positions per transaction.
            Configure the allocation basis below. Split percentages are defined at the
            position assignment level in the Assignments section.
          </p>
          <label className="credit-allocation-label" htmlFor="ca-allocation-basis">
            Allocation basis
          </label>
          <select
            id="ca-allocation-basis"
            className="credit-allocation-select"
            value={rules.allocationBasis ?? ''}
            onChange={(e) => handleAttributionChange('allocationBasis', e.target.value || null)}
          >
            <option value="">Select allocation basis</option>
            <option value="equal">Equal — credit divided equally among participants</option>
            <option value="custom">Custom — per-transaction split defined at deal time</option>
          </select>
        </div>
      );
    }

    if (form.creditingMode === 'territory') {
      return (
        <div>
          <label className="credit-allocation-label" htmlFor="ca-territory-id">
            Step 3 — Territory
          </label>
          <select
            id="ca-territory-id"
            className="credit-allocation-select"
            value={rules.territoryId ?? ''}
            onChange={(e) => handleAttributionChange('territoryId', e.target.value || null)}
            aria-invalid={Boolean(errorFor('attributionRules'))}
            aria-describedby={errorFor('attributionRules') ? errorId('attributionRules') : undefined}
          >
            <option value="">Select a territory</option>
            {territories.map((t) => (
              <option key={t.territory_id} value={t.territory_id}>
                {t.territory_code} — {t.territory_name}
              </option>
            ))}
          </select>
          {territories.length === 0 && (
            <p className="credit-allocation-option-copy" style={{ marginTop: 6 }}>
              No active territories found. Create territories in the Territory manager before authoring this plan.
            </p>
          )}
          <label className="credit-allocation-label" htmlFor="ca-allocation-pct" style={{ marginTop: 12 }}>
            Allocation percentage (0.0001 – 1.0000)
          </label>
          <input
            id="ca-allocation-pct"
            type="text"
            className="credit-allocation-select"
            value={rules.allocationPct ?? ''}
            placeholder="e.g. 1.0000"
            onChange={(e) => handleAttributionChange('allocationPct', e.target.value || null)}
          />
          <p className="credit-allocation-option-copy">
            Territory routing determines credit flow — it does not define territory assignment
            or territory-level performance measurement.
          </p>
        </div>
      );
    }

    if (form.creditingMode === 'overlay') {
      return (
        <div>
          <label className="credit-allocation-label" htmlFor="ca-overlay-rule-id">
            Step 3 — Overlay position
          </label>
          <select
            id="ca-overlay-rule-id"
            className="credit-allocation-select"
            value={rules.overlayPositionId ?? ''}
            onChange={(e) => handleAttributionChange('overlayPositionId', e.target.value || null)}
            aria-invalid={Boolean(errorFor('attributionRules'))}
            aria-describedby={errorFor('attributionRules') ? errorId('attributionRules') : undefined}
          >
            <option value="">Select an overlay position</option>
            {overlayRules.map((p) => (
              <option key={p.position_id} value={p.position_id}>
                {p.full_name}{p.role_name ? ` (${p.role_name})` : ''}
              </option>
            ))}
          </select>
          {overlayRules.length === 0 && (
            <p className="credit-allocation-option-copy" style={{ marginTop: 6 }}>
              No active positions found. Positions must exist before authoring an overlay plan.
            </p>
          )}
          <label className="credit-allocation-label" htmlFor="ca-attribution-weight" style={{ marginTop: 12 }}>
            Attribution weight (0.0001 – 1.0000)
          </label>
          <input
            id="ca-attribution-weight"
            type="text"
            className="credit-allocation-select"
            value={rules.attributionWeight ?? ''}
            placeholder="e.g. 0.2500"
            onChange={(e) => handleAttributionChange('attributionWeight', e.target.value || null)}
          />
        </div>
      );
    }

    if (form.creditingMode === 'channel') {
      return (
        <div>
          <label className="credit-allocation-label" htmlFor="ca-partner-comp-plan-id">
            Step 3 — Partner compensation plan
          </label>
          <select
            id="ca-partner-comp-plan-id"
            className="credit-allocation-select"
            value={rules.partnerCompPlanId ?? ''}
            onChange={(e) => handleAttributionChange('partnerCompPlanId', e.target.value ? Number(e.target.value) : null)}
            aria-invalid={Boolean(errorFor('attributionRules'))}
            aria-describedby={errorFor('attributionRules') ? errorId('attributionRules') : undefined}
          >
            <option value="">Select a partner compensation plan</option>
            {partnerPlans.map((pp) => (
              <option key={pp.partner_comp_plan_id} value={pp.partner_comp_plan_id}>
                {pp.plan_name} ({pp.commission_basis})
              </option>
            ))}
          </select>
          {partnerPlans.length === 0 && (
            <p className="credit-allocation-option-copy" style={{ marginTop: 6 }}>
              No active partner compensation plans found. Create one in the Channel Compensation manager.
            </p>
          )}
          <label className="credit-allocation-label" htmlFor="ca-channel-allocation-pct" style={{ marginTop: 12 }}>
            Channel allocation percentage (0.0001 – 1.0000)
          </label>
          <input
            id="ca-channel-allocation-pct"
            type="text"
            className="credit-allocation-select"
            value={rules.allocationPct ?? ''}
            placeholder="e.g. 0.1000"
            onChange={(e) => handleAttributionChange('allocationPct', e.target.value || null)}
          />
          <label className="credit-allocation-label" htmlFor="ca-commission-basis" style={{ marginTop: 12 }}>
            Commission basis
          </label>
          <select
            id="ca-commission-basis"
            className="credit-allocation-select"
            value={rules.commissionBasis ?? ''}
            onChange={(e) => handleAttributionChange('commissionBasis', e.target.value || null)}
          >
            {COMMISSION_BASIS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      );
    }

    return null;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <section
      className="modifier-manager-card credit-allocation-section"
      aria-labelledby="credit-allocation-heading"
    >
      <style>{`
        .credit-allocation-section {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          color: rgb(var(--color-text));
          font-family: "IBM Plex Sans", sans-serif;
        }
        .credit-allocation-section .modifier-manager-header {
          align-items: center;
          border-bottom: 1px solid rgb(var(--color-border));
          display: flex;
          gap: 16px;
          justify-content: space-between;
          padding: 16px;
        }
        .credit-allocation-section .modifier-manager-body {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
        }
        .credit-allocation-heading { font-size: 18px; font-weight: 600; margin: 0; }
        .credit-allocation-summary { color: rgb(var(--color-text-muted)); font-size: 13px; margin: 4px 0 0; }
        .credit-allocation-fieldset { border: 0; margin: 0; padding: 0; }
        .credit-allocation-legend, .credit-allocation-label {
          display: block; font-size: 13px; font-weight: 600; margin-bottom: 8px;
        }
        .credit-allocation-options {
          display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .credit-allocation-option {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          border-radius: 8px; cursor: pointer; display: flex; gap: 9px; padding: 12px;
        }
        .credit-allocation-option[data-selected="true"] { background: rgb(var(--color-selected-bg)); }
        .credit-allocation-option-title { display: block; font-size: 14px; font-weight: 600; }
        .credit-allocation-option-copy {
          color: rgb(var(--color-text-muted)); display: block;
          font-size: 12px; line-height: 1.45; margin-top: 2px;
        }
        .credit-allocation-select {
          background: rgb(var(--color-surface)); border: 1px solid rgb(var(--color-border));
          border-radius: 6px; color: rgb(var(--color-text)); font: inherit;
          min-height: 40px; padding: 8px 10px; width: 100%;
        }
        .credit-allocation-error { color: rgb(var(--color-danger)); font-size: 12px; margin: 5px 0 0; }
        .credit-allocation-section .modifier-manager-info,
        .credit-allocation-section .modifier-manager-warning,
        .credit-allocation-section .modifier-manager-readonly-note {
          border: 1px solid rgb(var(--color-border)); border-radius: 8px;
          font-size: 13px; line-height: 1.5; margin: 0; padding: 10px 12px;
        }
        .credit-allocation-section .modifier-manager-info { background: rgb(var(--color-info) / 0.08); }
        .credit-allocation-section .modifier-manager-warning { background: rgb(var(--color-warning) / 0.08); }
        .credit-allocation-section .modifier-manager-readonly-note {
          background: rgb(var(--color-surface-muted)); color: rgb(var(--color-text-muted));
        }
        .credit-allocation-rule-summary { background: rgb(var(--color-surface-muted)); border-radius: 8px; padding: 12px; }
        .credit-allocation-rule-summary dt { color: rgb(var(--color-text-muted)); font-size: 12px; font-weight: 500; }
        .credit-allocation-rule-summary dd { font-size: 14px; margin: 2px 0 10px; }
        .credit-allocation-rule-summary dd:last-child { margin-bottom: 0; }
        @media (max-width: 720px) {
          .credit-allocation-options { grid-template-columns: 1fr; }
          .credit-allocation-section .modifier-manager-header {
            align-items: flex-start; flex-direction: column;
          }
        }
      `}</style>

      <header className="modifier-manager-header">
        <div>
          <h2 id="credit-allocation-heading" className="credit-allocation-heading">
            Credit Allocation
          </h2>
          <p className="credit-allocation-summary">{summaryType}</p>
        </div>
        {!readOnly && (
          <button
            type="button"
            className="text-button"
            aria-expanded={expanded}
            aria-controls="credit-allocation-configuration"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? 'Hide credit configuration' : 'Configure shared or routed credit ▾'}
          </button>
        )}
      </header>

      {(expanded || readOnly) && (
        <div id="credit-allocation-configuration" className="modifier-manager-body">
          {readOnly && (
            <p className="modifier-manager-readonly-note" role="note">
              Credit Allocation is read-only for this plan version. Changes require a successor amendment.
            </p>
          )}

          <p className="modifier-manager-info" role="note">
            Credit Allocation determines who receives credit for an economic event.
            Assignment Scope determines who is eligible for this plan.
          </p>

          {readOnly ? (
            <>
              <dl className="credit-allocation-rule-summary">
                <dt>Crediting mode</dt>
                <dd>
                  {MODE_OPTIONS.find((o) => o.value === activeGroup)?.label ?? 'Individual'}
                </dd>
                <dt>Credit allocation type</dt>
                <dd>
                  {form.creditingMode === 'individual' || !form.creditingMode
                    ? 'Individual · 100% credit'
                    : SPLIT_TYPE_OPTIONS.find((o) => o.value === form.creditingMode)?.label
                      ?? form.creditingMode}
                </dd>
                <dt>Rule configuration</dt>
                <dd>
                  {form.attributionRules
                    ? 'Configured'
                    : (!form.creditingMode || form.creditingMode === 'individual')
                      ? 'No additional routing rules required'
                      : 'Not specified'}
                </dd>
              </dl>
              {form.creditingMode === 'territory' && (
                <p className="modifier-manager-warning" role="note">
                  Territory credit routing determines how credit flows based on territory rules.
                  It does not define territory assignment and it does not define territory-level
                  performance measurement.
                </p>
              )}
            </>
          ) : (
            <>
              {/* Step 1 — Crediting mode group */}
              <fieldset className="credit-allocation-fieldset">
                <legend className="credit-allocation-legend">Step 1 — Crediting mode</legend>
                <div className="credit-allocation-options">
                  {MODE_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="credit-allocation-option"
                      data-selected={activeGroup === option.value ? 'true' : 'false'}
                    >
                      <input
                        type="radio"
                        name="crediting-mode"
                        value={option.value}
                        checked={activeGroup === option.value}
                        onChange={() => handleModeChange(option)}
                      />
                      <span>
                        <span className="credit-allocation-option-title">{option.label}</span>
                        <span className="credit-allocation-option-copy">{option.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {errorFor('creditingMode') && (
                  <p id={errorId('creditingMode')} className="credit-allocation-error" role="alert">
                    {errorFor('creditingMode')}
                  </p>
                )}
              </fieldset>

              {/* Step 2 — Type selector (split group only — overlay vs channel) */}
              {isSplitGroup && (
                <div>
                  <label className="credit-allocation-label" htmlFor="credit-allocation-type">
                    Step 2 — Credit allocation type
                  </label>
                  <select
                    id="credit-allocation-type"
                    className="credit-allocation-select"
                    value={form.layer0Type ?? ''}
                    onChange={handleTypeChange}
                    aria-invalid={Boolean(errorFor('layer0Type'))}
                    aria-describedby={errorFor('layer0Type') ? errorId('layer0Type') : undefined}
                  >
                    <option value="">Select a type</option>
                    {SPLIT_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {errorFor('layer0Type') && (
                    <p id={errorId('layer0Type')} className="credit-allocation-error" role="alert">
                      {errorFor('layer0Type')}
                    </p>
                  )}
                  {form.layer0Type && (
                    <p className="credit-allocation-option-copy">
                      {SPLIT_TYPE_OPTIONS.find((o) => o.value === form.layer0Type)?.description}
                    </p>
                  )}
                </div>
              )}

              {/* Territory routing note */}
              {form.creditingMode === 'territory' && (
                <p className="modifier-manager-warning" role="note">
                  Territory credit routing determines how credit flows based on territory rules.
                  It does not define territory assignment and it does not define territory-level
                  performance measurement.
                </p>
              )}

              {/* Step 3 — Attribution rules form (per creditingMode) */}
              {showStep3 && (
                <div>
                  {renderAttributionForm()}
                  {errorFor('attributionRules') && (
                    <p id={errorId('attributionRules')} className="credit-allocation-error" role="alert">
                      {errorFor('attributionRules')}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
});

CreditAllocationSection.displayName = 'CreditAllocationSection';

CreditAllocationSection.propTypes = {
  plan: PropTypes.shape({
    planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  layer0Config: PropTypes.shape({
    creditingMode:    PropTypes.oneOf(['individual', 'deal_split', 'territory', 'overlay', 'channel']),
    layer0Type:       PropTypes.oneOf(['deal_split', 'territory', 'overlay', 'channel']),
    attributionRules: PropTypes.object,
    isActive:         PropTypes.bool.isRequired,
  }).isRequired,
  readOnly:         PropTypes.bool.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string),
};

export default CreditAllocationSection;
